import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  IconButton,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Alert,
  Divider,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import { ContextSession } from "../../services/ContextSession";

const RegistrarCorrectivo = () => {
  // Hooks y estados
  const navigate = useNavigate();
  const { sessionData } = useContext(ContextSession);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: Ingresar placa, 2: Mostrar datos y formulario
  const [vehiculoData, setVehiculoData] = useState(null);
  const [placa, setPlaca] = useState("");
  const [vin, setVin] = useState("");
  const [placaError, setPlacaError] = useState(false);
  const [vinError, setVinError] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [talleres, setTalleres] = useState([]);
  const [tipoBusqueda, setTipoBusqueda] = useState("placa"); // "placa" o "vin"
  const [kilometrajeError, setKilometrajeError] = useState("");

  // Estado para notificaciones
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Estado del registro de mantenimiento
  const [registro, setRegistro] = useState({
    vehiculo: "",
    alerta: null,
    tipo_mantenimiento: "Correctivo",
    descripcion: "",
    kilometraje: "",
    fecha_realizacion: new Date().toISOString().split("T")[0],
    observaciones: "",
    taller: "",
    evidencia: [],
    usuario_id: "",
    usuario_nombre: "",
  });

  // Obtener usuario_id y nombre de la sesión
  useEffect(() => {
    if (sessionData) {
      const userInfo = getUserInfo();

      console.log("Usuario para mantenimiento:", userInfo);

      if (userInfo.id) {
        setRegistro((prev) => ({
          ...prev,
          usuario_id: userInfo.id.toString(),
          usuario_nombre: userInfo.name,
        }));
      }
    }
  }, [sessionData]);

  useEffect(() => {
    const cargarTalleres = async () => {
      try {
        const res = await fetch(
          "https://bei360.injupemp.gob.hn:8444/api/taller/"
        );
        if (!res.ok) throw new Error("Error al cargar talleres");
        const data = await res.json();
        setTalleres(data);
      } catch (err) {
        console.error("Error al obtener talleres:", err);
        showSnackbar(`Error al cargar talleres: ${err.message}`, "error");
      }
    };
    cargarTalleres();
  }, []);

  // Función para obtener información del usuario
  const getUserInfo = (userData = sessionData) => {
    if (!userData) return { id: null, name: "No identificado" };

    // Extraer ID del usuario
    const getId = (data) =>
      data.userId ||
      data.user_id ||
      data.id ||
      data.usuario_id ||
      (data.user && data.user.id) ||
      (data.usuario && data.usuario.id);

    // Extraer nombre del usuario
    const getName = (data) => {
      const name =
        data.userName ||
        data.username ||
        data.nombre ||
        (data.user && data.user.name) ||
        (data.usuario && data.usuario.nombre);

      // Si tenemos nombre y apellido, los combinamos
      if (data.nombre && data.apellido) {
        return `${data.nombre} ${data.apellido}`.trim();
      }

      return name || "Usuario No Identificado";
    };

    return {
      id: getId(userData),
      name: getName(userData),
    };
  };

  // Mostrar notificación
  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  // Cerrar notificación
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Función para validar kilometraje en tiempo real
  const validarKilometraje = (value) => {
    if (!vehiculoData || !value) {
      setKilometrajeError("");
      return;
    }

    const kmIngresado = parseFloat(value);
    const kmVehiculo = parseFloat(vehiculoData.kilometraje);

    if (isNaN(kmIngresado)) {
      setKilometrajeError("El kilometraje debe ser un número válido");
      return;
    }

    if (kmIngresado < kmVehiculo) {
      setKilometrajeError(
        `El kilometraje no puede ser menor al actual del vehículo (${kmVehiculo.toLocaleString()} km)`
      );
    } else {
      setKilometrajeError("");
    }
  };

  // Función para buscar vehículo por placa o VIN
  const buscarVehiculo = async () => {
    // Validaciones según el tipo de búsqueda
    if (tipoBusqueda === "placa" && !placa.trim()) {
      setPlacaError(true);
      setVinError(false);
      return;
    }

    if (tipoBusqueda === "vin" && !vin.trim()) {
      setVinError(true);
      setPlacaError(false);
      return;
    }

    // Validar que el VIN tenga exactamente 4 dígitos
    if (tipoBusqueda === "vin" && !/^\d{4}$/.test(vin.trim())) {
      setVinError(true);
      setError("El VIN debe contener exactamente 4 dígitos");
      return;
    }

    setIsLoading(true);
    setError(null);
    setPlacaError(false);
    setVinError(false);
    setKilometrajeError("");

    try {
      let url = "";
      if (tipoBusqueda === "placa") {
        url = `https://bei360.injupemp.gob.hn:8444/api/vehiculos/${placa.trim()}/`;
      } else {
        url = `https://bei360.injupemp.gob.hn:8444/api/vehiculos/vin/${vin.trim()}/`;
      }

      const res = await fetch(url);
      if (!res.ok) throw new Error("Vehículo no encontrado");

      const data = await res.json();

      // Si la búsqueda por VIN devuelve un array, tomar el primer elemento
      const vehiculo = Array.isArray(data) ? data[0] : data;

      if (!vehiculo) throw new Error("Vehículo no encontrado");

      // Mapear el kilometraje de la API al estado
      const kilometrajeVehiculo = parseFloat(vehiculo.kilometraje);

      setVehiculoData(vehiculo);
      setRegistro((prev) => ({
        ...prev,
        vehiculo: vehiculo.id,
        kilometraje: kilometrajeVehiculo.toString(), // Pre-cargar con el kilometraje actual
      }));
      setStep(2);

      showSnackbar("Vehículo encontrado exitosamente", "success");
    } catch (err) {
      setError(err.message);
      showSnackbar(`Error: ${err.message}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Manejador de cambios en el tipo de búsqueda
  const handleTipoBusquedaChange = (event, newTipo) => {
    if (newTipo !== null) {
      setTipoBusqueda(newTipo);
      // Limpiar errores y campos al cambiar el tipo de búsqueda
      setPlacaError(false);
      setVinError(false);
      setError(null);
      setPlaca("");
      setVin("");
    }
  };

  // Manejador de cambios en los campos del formulario
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "evidencia") {
      const archivos = Array.from(files);
      setRegistro((prev) => ({ ...prev, evidencia: archivos }));
      setAttachments(archivos);
    } else {
      setRegistro((prev) => ({ ...prev, [name]: value }));

      // Validación en tiempo real para kilometraje
      if (name === "kilometraje") {
        validarKilometraje(value);
      }
    }
  };

  // Eliminar archivo adjunto
  const removeAttachment = (index) => {
    const nuevos = [...registro.evidencia];
    nuevos.splice(index, 1);
    setRegistro((prev) => ({ ...prev, evidencia: nuevos }));
    setAttachments(nuevos);
  };

  // Enviar formulario
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar kilometraje antes de enviar
    if (kilometrajeError) {
      showSnackbar(
        "Por favor corrija el error en el campo de kilometraje",
        "error"
      );
      return;
    }

    // Obtener usuario actual
    const userInfo = getUserInfo();

    // Validar que existe usuario_id
    if (!userInfo.id) {
      showSnackbar(
        "No se pudo identificar al usuario. Por favor, inicie sesión nuevamente.",
        "error"
      );
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Preparar datos para enviar - CORREGIDO: usar kilometraje en lugar de kilometraje_actual
      const datosEnvio = {
        vehiculo: parseInt(registro.vehiculo),
        tipo_mantenimiento: registro.tipo_mantenimiento,
        descripcion: registro.descripcion,
        kilometraje: parseFloat(registro.kilometraje), // CORREGIDO: enviar como kilometraje
        fecha_realizacion: registro.fecha_realizacion,
        observaciones: registro.observaciones,
        taller: parseInt(registro.taller),
        usuario_id: userInfo.id.toString(),
        usuario_nombre: userInfo.name,
      };

      // Solo incluir alerta si tiene valor
      if (registro.alerta) {
        datosEnvio.alerta = parseInt(registro.alerta);
      }

      console.log("Datos a enviar:", datosEnvio);

      // 1. Crear registro en historial
      const res = await fetch(
        "https://bei360.injupemp.gob.hn:8444/api/historial/",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(datosEnvio),
        }
      );

      if (!res.ok) {
        const errorData = await res.json();
        console.error("Error response:", errorData);

        // Manejar errores de validación de kilometraje
        if (errorData.kilometraje) {
          throw new Error(`Error de validación: ${errorData.kilometraje[0]}`);
        }

        throw new Error(
          errorData.detail ||
            errorData.message ||
            JSON.stringify(errorData) ||
            "Error al crear el registro de historial"
        );
      }

      const historial = await res.json();
      console.log("Historial creado:", historial);

      // 2. Subir evidencias
      if (registro.evidencia?.length > 0) {
        for (const archivo of registro.evidencia) {
          const formData = new FormData();
          formData.append("archivo", archivo);
          formData.append("historial", historial.id);
          formData.append("creado_por", userInfo.name || "frontend");

          const response = await fetch(
            "https://bei360.injupemp.gob.hn:8444/api/evidencias/",
            {
              method: "POST",
              body: formData,
              headers: {
                Accept: "application/json",
              },
            }
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || "Error al subir el archivo");
          }

          const result = await response.json();
          console.log("Evidencia subida:", result);
        }
      }

      showSnackbar(
        `Mantenimiento correctivo registrado exitosamente por ${userInfo.name}`,
        "success"
      );

      // Navegar después de un breve delay para que se vea el mensaje
      setTimeout(() => {
        navigate("/Flotas/HistorialMantenimientos");
      }, 2000);
    } catch (err) {
      console.error("Error:", err);
      setError(err.message);
      showSnackbar(`Error: ${err.message}`, "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Volver al paso de búsqueda
  const volverABuscar = () => {
    setStep(1);
    setVehiculoData(null);
    setPlaca("");
    setVin("");
    setError(null);
    setPlacaError(false);
    setVinError(false);
    setKilometrajeError("");
  };

  // Obtener información del usuario para mostrar
  const userInfo = getUserInfo();

  // Renderizado del componente
  return (
    <Container maxWidth="md" sx={{ mt: 2, mb: 2 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" fontWeight="bold" mb={2}>
          Registrar Mantenimiento Correctivo
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {step === 1 ? (
          // Paso 1: Seleccionar tipo de búsqueda e ingresar dato
          <Box>
            <Typography variant="body1" mb={2}>
              Seleccione el método de búsqueda e ingrese los datos del vehículo
            </Typography>

            {/* Selector de tipo de búsqueda */}
            <Box mb={3}>
              <Typography variant="body2" color="textSecondary" mb={1}>
                Buscar por:
              </Typography>
              <ToggleButtonGroup
                value={tipoBusqueda}
                exclusive
                onChange={handleTipoBusquedaChange}
                fullWidth
              >
                <ToggleButton value="placa">Placa</ToggleButton>
                <ToggleButton value="vin">
                  Últimos 4 dígitos del VIN
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8}>
                {tipoBusqueda === "placa" ? (
                  <TextField
                    label="Placa del vehículo"
                    value={placa}
                    onChange={(e) => setPlaca(e.target.value)}
                    fullWidth
                    required
                    error={placaError}
                    helperText={placaError ? "Debe ingresar una placa" : ""}
                    disabled={isLoading}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        buscarVehiculo();
                      }
                    }}
                  />
                ) : (
                  <TextField
                    label="Últimos 4 dígitos del VIN"
                    value={vin}
                    onChange={(e) =>
                      setVin(e.target.value.replace(/\D/g, "").slice(0, 4))
                    }
                    fullWidth
                    required
                    error={vinError}
                    helperText={
                      vinError
                        ? "Debe ingresar exactamente 4 dígitos"
                        : "Ingrese solo los últimos 4 dígitos del VIN"
                    }
                    disabled={isLoading}
                    inputProps={{ maxLength: 4 }}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        buscarVehiculo();
                      }
                    }}
                  />
                )}
              </Grid>

              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  onClick={buscarVehiculo}
                  disabled={isLoading || !userInfo.id}
                  fullWidth
                  sx={{ py: 1.5, backgroundColor: "#19438C" }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    "Buscar Vehículo"
                  )}
                </Button>
                {!userInfo.id && (
                  <Typography
                    variant="caption"
                    color="error"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    Espere identificando usuario...
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        ) : (
          // Paso 2: Mostrar datos del vehículo y formulario
          <Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={volverABuscar}
              sx={{ mb: 2 }}
            >
              Volver a buscar
            </Button>

            {vehiculoData && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Datos del Vehículo
                  </Typography>

                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        Placa:
                      </Typography>
                      <Typography variant="body1">
                        {vehiculoData.placa}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        VIN:
                      </Typography>
                      <Typography variant="body1">
                        {vehiculoData.vin}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        Marca:
                      </Typography>
                      <Typography variant="body1">
                        {vehiculoData.marca}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        Modelo:
                      </Typography>
                      <Typography variant="body1">
                        {vehiculoData.modelo}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        Año:
                      </Typography>
                      <Typography variant="body1">
                        {vehiculoData.año}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="textSecondary">
                        Kilometraje actual:
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {parseFloat(vehiculoData.kilometraje).toLocaleString()}{" "}
                        km
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            <Typography variant="h6" gutterBottom>
              Datos del Mantenimiento Correctivo
            </Typography>

            <form onSubmit={handleSubmit}>
              <TextField
                name="descripcion"
                label="Descripción"
                value={registro.descripcion}
                onChange={handleChange}
                fullWidth
                required
                multiline
                rows={3}
                margin="normal"
              />

              <TextField
                name="kilometraje"
                label="Kilometraje"
                type="number"
                value={registro.kilometraje}
                onChange={handleChange}
                fullWidth
                required
                margin="normal"
                InputLabelProps={{ shrink: true }}
                error={!!kilometrajeError}
                helperText={
                  kilometrajeError ||
                  (vehiculoData
                    ? `Mínimo: ${parseFloat(
                        vehiculoData.kilometraje
                      ).toLocaleString()} km`
                    : "Ingrese el kilometraje")
                }
                inputProps={{
                  min: vehiculoData ? parseFloat(vehiculoData.kilometraje) : 0,
                  step: 1,
                }}
              />

              <TextField
                name="fecha_realizacion"
                label="Fecha de realización"
                type="date"
                value={registro.fecha_realizacion}
                onChange={handleChange}
                fullWidth
                required
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                select
                name="taller"
                label="Seleccione el taller"
                value={registro.taller}
                onChange={handleChange}
                fullWidth
                required
                margin="normal"
                InputLabelProps={{ shrink: true }}
              >
                {talleres.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.nombre} — {t.direccion}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                name="observaciones"
                label="Observaciones"
                value={registro.observaciones}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
                margin="normal"
              />

              {/* Adjuntos */}
              <Box mt={2}>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  disabled={isLoading}
                  color="success"
                >
                  Subir Evidencia
                  <input
                    type="file"
                    name="evidencia"
                    accept="image/*,.pdf"
                    hidden
                    multiple
                    onChange={handleChange}
                  />
                </Button>

                {attachments.length > 0 && (
                  <Box mt={1}>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Archivos adjuntos:
                    </Typography>

                    <ul style={{ listStyle: "none", padding: 0 }}>
                      {attachments.map((file, i) => (
                        <li
                          key={i}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: 8,
                          }}
                        >
                          <span>{file.name}</span>
                          <IconButton
                            color="error"
                            onClick={() => removeAttachment(i)}
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </li>
                      ))}
                    </ul>
                  </Box>
                )}
              </Box>

              <Box display="flex" justifyContent="flex-end" mt={3}>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ backgroundColor: "#19438C", color: "white" }}
                  disabled={isLoading || !userInfo.id || !!kilometrajeError}
                >
                  {isLoading ? (
                    <CircularProgress size={20} />
                  ) : userInfo.id ? (
                    `Registrar Mantenimiento`
                  ) : (
                    "Espere..."
                  )}
                </Button>
              </Box>
            </form>
          </Box>
        )}
      </Paper>

      {/* Notificación */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default RegistrarCorrectivo;
